import { useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Cancel01Icon } from '@hugeicons/core-solid-rounded'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { FieldPath } from 'react-hook-form'

import {
  ArrowDown01Icon,
  ArrowRight01Icon
} from '@hugeicons/core-stroke-rounded'

import * as Form from '@/features/form'
import ColorPicker from '@/components/ui/ColorPicker'
import Title from './Title'
import InputTile from './InputTile'
import { FormData } from '../hooks/useBranding'

export const defaultColors = {
  appBackground: '#EEEEEE',
  appForeground: '#333333',
  navBackground: '#FFFFFF',
  txtRegular: '#333333',
  txtLight: '#555555',
  txtPlaceholder: '#D9D9D9',
  error: '#FF746C'
}

interface ColorFieldProps {
  defaultColor: string
  name: string
  label: string
  description?: string
}

export function ColorField(props: ColorFieldProps) {
  const { defaultColor, name, label, description } = props

  return (
    <Form.Field
      name={name}
      label={label}
      size="small"
      description={description}
    >
      {({ renderProps: { value, onChange, ...props } }) => (
        <div className="flex flex-row items-center gap-2 text-regular text-text">
          <div className="flex cursor-pointer flex-row items-center gap-4 text-regular text-text">
            <ColorPicker
              value={value?.toUpperCase() ?? defaultColor}
              onChange={onChange}
              {...props}
            />
          </div>
          {value && value.toUpperCase() !== defaultColor.toUpperCase() && (
            <button tabIndex={0} onClick={() => onChange(defaultColor)}>
              <HugeiconsIcon icon={Cancel01Icon} className="size-5" />
            </button>
          )}
        </div>
      )}
    </Form.Field>
  )
}

export default function Colors() {
  const { t } = useLingui()
  const [showAdvanced, setShowAdvanced] = useState(false)

  return (
    <InputTile>
      <Title
        title={t`Colors`}
        description={t`The colors of your branding, these will be used in the GuestApp.`}
      />
      <Form.Field<FormData>
        name="colors.primary"
        label={t`Main Brand Color`}
        size="small"
        description={t({
          comment:
            'Description for the primary/main brand color of the user, with the addition that light colors won`t work as good.',
          message:
            'The main branding color, this could be your logo color. We recommend a not to light color.'
        })}
      >
        {({
          getValues,
          setValue,
          renderProps: { value, onChange, ...props }
        }) => (
          <div className="flex cursor-pointer flex-row items-center gap-4 text-regular text-text">
            <ColorPicker
              value={value.toUpperCase()}
              onChange={newColor => {
                const values = getValues()
                const primaryBefore = values.colors.primary

                Object.entries(values.colors).forEach(([field, color]) => {
                  if (color === primaryBefore) {
                    setValue(`colors.${field}` as FieldPath<FormData>, newColor)
                  }
                })

                onChange(newColor)
              }}
              {...props}
            />
          </div>
        )}
      </Form.Field>
      <button
        className="flex flex-row items-center gap-2 rounded-small"
        type="button"
        tabIndex={0}
        onClick={() => setShowAdvanced(show => !show)}
      >
        {showAdvanced ? (
          <HugeiconsIcon icon={ArrowDown01Icon} className="size-4" />
        ) : (
          <HugeiconsIcon icon={ArrowRight01Icon} className="size-4" />
        )}
        <span className="text-regular text-text">
          <Trans>Advanced Settings</Trans>
        </span>
      </button>
      <div
        className={clsx('flex flex-col gap-2 pl-6', !showAdvanced && 'hidden')}
      >
        <ColorField
          name="colors.navBackground"
          label={t`Navigationbar Background Color`}
          defaultColor={defaultColors.navBackground}
        />
        <Form.Data<FormData>>
          {({ colors: { primary } }) => {
            return (
              <ColorField
                name="colors.navForeground"
                label={t`Navigationbar Foreground/Text/Icon Color`}
                defaultColor={primary}
              />
            )
          }}
        </Form.Data>
        <ColorField
          name="colors.appBackground"
          label={t`App Background Color`}
          defaultColor={defaultColors.appBackground}
        />
        <ColorField
          name="colors.appForeground"
          label={t`App Foreground/Text/Icon Color`}
          defaultColor={defaultColors.appForeground}
        />
        <Form.Data<FormData>>
          {({ colors: { primary } }) => {
            return (
              <ColorField
                name="colors.statusBar"
                label={t`Browser UI`}
                description={t`Defines what color the top and bottom of the browser UI has. This is not supported by all browsers/phones.`}
                defaultColor={primary}
              />
            )
          }}
        </Form.Data>
        <Form.Data<FormData>>
          {({ colors: { primary } }) => {
            return (
              <ColorField
                name="colors.btnRegular"
                label={t`Action Color`}
                defaultColor={primary}
              />
            )
          }}
        </Form.Data>
        <ColorField
          name="colors.txtRegular"
          label={t`Text Color`}
          defaultColor={defaultColors.txtRegular}
        />
        <ColorField
          name="colors.txtLight"
          label={t`Secondary Text Color`}
          defaultColor={defaultColors.txtLight}
        />
        <ColorField
          name="colors.txtPlaceholder"
          label={t`Placeholder Text Color`}
          defaultColor={defaultColors.txtPlaceholder}
        />
        <Form.Data<FormData>>
          {({ colors: { primary } }) => {
            return (
              <ColorField
                name="colors.banner"
                label={t`Banner Color`}
                defaultColor={primary}
              />
            )
          }}
        </Form.Data>
        <Form.Data<FormData>>
          {({ colors: { primary } }) => {
            return (
              <ColorField
                name="colors.info"
                label={t`Information Color`}
                defaultColor={primary}
              />
            )
          }}
        </Form.Data>
        <ColorField
          name="colors.error"
          label={t`Error Color`}
          defaultColor={defaultColors.error}
        />
      </div>
    </InputTile>
  )
}
